import { ChartAreaIcon } from '@/src/assets/icons'
import { DatePicker } from '@/src/components/DatePicker/DatePicker'
import { GranularitySelect } from '@/src/components/GranularitySelect'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { useLoadedStrategyMetadata } from '@/src/features/procurement-and-markets/load-planning/hooks/LoadPlanning.hooks'
import type { LoadPlanningAdjustmentFormValidated } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustment.types'
import { getFifteenMinutesDefaultTimeRange } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/preview-graph/LoadPlanningPreview.utils'
import { LoadPlanningPreviewGraph } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/preview-graph/LoadPlanningPreviewGraph'
import { LoadPlanningPreviewKpiBoxes } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/preview-graph/LoadPlanningPreviewKpiBoxes'
import {
  generateDatePickerPreset,
  getDatePickerTypeFromGranularity,
  getDateRangeOfYear,
  getGranularityOptions,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanning.utils'
import { appColors } from '@/src/theme'
import { Box, Spinner } from '@chakra-ui/react'
import { FlexCol, FlexRow } from '@components'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mapToEnergyTypeDto } from '../../../details/types/adjustment.utils'

interface LoadPlanningPreviewVisualsProps {
  dataToPreview?: LoadPlanningAdjustmentFormValidated
}

export const LoadPlanningPreviewVisuals = React.memo(({ dataToPreview }: LoadPlanningPreviewVisualsProps) => {
  const { t } = useTranslation()
  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const { companyPgId } = useLocationsContext()

  const [granularity, setGranularity] = useState<GranularityEnum>(GranularityEnum.ONE_MONTH)

  const dateRangeLimits = useMemo(() => getDateRangeOfYear(strategyMetadata?.year), [strategyMetadata?.year])

  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>(() => ({
    from: dateRangeLimits.minDate || new Date(),
    to: dateRangeLimits.maxDate || new Date(),
  }))

  if (!strategyMetadata || !companyPgId) return <Spinner />

  const onGranularityChange = (e: GranularityEnum) => {
    setGranularity(e)
    if (e === GranularityEnum.FIFTEEN_MINUTES) {
      const [from, to] = getFifteenMinutesDefaultTimeRange(dataToPreview, Number(strategyMetadata.year))
      setDateRange({ from, to })
    }
    if (e === GranularityEnum.ONE_MONTH && dateRangeLimits.minDate && dateRangeLimits.maxDate) {
      setDateRange({ from: dateRangeLimits.minDate, to: dateRangeLimits.maxDate })
    }
  }

  const onDateChange = ([from, to]: [Date | null, Date | null]) => {
    if (from && to) {
      setDateRange({ from, to })
    }
  }

  if (!dataToPreview) {
    return (
      <FlexCol w={'100%'} h={'100%'}>
        <LoadPlanningPreviewKpiBoxes dataToPreview={dataToPreview} />
        <FlexCol w={'100%'} h={'100%'} justifyContent={'center'} alignItems={'center'}>
          <ChartAreaIcon fontSize={'61px'} color={appColors.secondaryForeground} />
          <Box>{t('components.procurement.forecasting.modal.form.preview_empty_state')}</Box>
        </FlexCol>
      </FlexCol>
    )
  }

  return (
    <FlexCol gap={4} width='100%' height='100%'>
      <LoadPlanningPreviewKpiBoxes dataToPreview={dataToPreview} />
      <FlexRow gap={4} justifyContent={'flex-end'} width={'100%'}>
        <GranularitySelect
          value={granularity}
          onChange={onGranularityChange}
          options={getGranularityOptions(mapToEnergyTypeDto(strategyMetadata.energyType), t)}
        />
        <DatePicker
          pickerType={getDatePickerTypeFromGranularity(granularity)}
          isRange={true}
          value={[dateRange.from, dateRange.to]}
          onChange={onDateChange}
          minDate={dateRangeLimits.minDate}
          maxDate={dateRangeLimits.maxDate}
          presets={
            granularity === GranularityEnum.FIFTEEN_MINUTES || granularity === GranularityEnum.ONE_HOUR
              ? generateDatePickerPreset(Number(strategyMetadata.year))
              : undefined
          }
        />
      </FlexRow>

      <FlexCol flex={1}>
        <LoadPlanningPreviewGraph dataToPreview={dataToPreview} granularity={granularity} dateRange={dateRange} />
      </FlexCol>
    </FlexCol>
  )
})
